import React, { useState } from "react";
import { MagnifyingGlass, MapPin, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const NICHE_SUGGESTIONS = [
  "dentists",
  "cafes",
  "plumbers",
  "yoga studios",
  "law firms",
  "hair salons",
];

export function SearchHero({ onScan, loading }) {
  const [niche, setNiche] = useState("");
  const [area, setArea] = useState("");
  const [cap, setCap] = useState(40);

  const submit = (e) => {
    e.preventDefault();
    if (!niche.trim() || !area.trim()) {
      toast.error("Please fill in both niche and area.");
      return;
    }
    onScan({ niche: niche.trim(), area: area.trim(), cap });
  };

  return (
    <section className="relative overflow-hidden border-b border-border/60 bg-white">
      <div className="gbp-grid-bg pointer-events-none absolute inset-0 opacity-70" />
      <div className="pointer-events-none absolute -top-24 right-[-10%] h-72 w-72 rounded-full bg-electric/10 blur-3xl" />
      <div className="relative mx-auto max-w-6xl px-6 py-12 md:px-10 md:py-16">
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-electric/30 bg-electric-soft px-2.5 py-1 text-xs font-medium text-electric">
            <span className="h-1.5 w-1.5 rounded-full bg-electric" />
            Local SEO / Outreach tool
          </span>
          <span className="text-xs text-muted-foreground">
            Local SEO outreach edition
          </span>
        </div>

        <h1
          data-testid="hero-title"
          className="font-display text-4xl font-extrabold tracking-tight text-ink sm:text-5xl lg:text-6xl"
        >
          Find businesses ignoring
          <br className="hidden sm:block" />
          <span className="text-electric">Google Posts.</span>
        </h1>
        <p className="mt-4 max-w-2xl font-sans text-base text-muted-foreground md:text-lg">
          Scan Google Business Profiles by niche &amp; area to see who&apos;s
          active, who&apos;s dormant, and who&apos;s a perfect outreach target.
        </p>

        <form
          onSubmit={submit}
          data-testid="scan-form"
          className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-[1.15fr_1.15fr_auto_auto] md:items-stretch"
        >
          <label className="group flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2.5 transition-colors focus-within:border-electric focus-within:ring-2 focus-within:ring-electric/30">
            <MagnifyingGlass size={18} weight="bold" className="text-muted-foreground" />
            <input
              data-testid="niche-input"
              value={niche}
              onChange={(e) => setNiche(e.target.value)}
              placeholder="Niche  ·  e.g. dentists"
              className="w-full bg-transparent font-sans text-sm text-ink placeholder:text-muted-foreground focus:outline-none"
            />
          </label>
          <label className="group flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2.5 transition-colors focus-within:border-electric focus-within:ring-2 focus-within:ring-electric/30">
            <MapPin size={18} weight="bold" className="text-muted-foreground" />
            <input
              data-testid="area-input"
              value={area}
              onChange={(e) => setArea(e.target.value)}
              placeholder="Area  ·  e.g. Dandenong, VIC"
              className="w-full bg-transparent font-sans text-sm text-ink placeholder:text-muted-foreground focus:outline-none"
            />
          </label>
          <div className="flex items-center rounded-md border border-border bg-white px-3">
            <label className="mr-2 whitespace-nowrap font-mono text-xs uppercase tracking-wider text-muted-foreground">
              Cap
            </label>
            <select
              data-testid="cap-select"
              value={cap}
              onChange={(e) => setCap(Number(e.target.value))}
              className="bg-transparent py-2.5 font-mono text-sm text-ink focus:outline-none"
            >
              <option value={20}>20</option>
              <option value={40}>40</option>
              <option value={60}>60</option>
            </select>
          </div>
          <button
            data-testid="scan-submit"
            type="submit"
            disabled={loading}
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-electric px-5 py-2.5 font-sans text-sm font-semibold text-white shadow-[0_1px_0_rgba(255,255,255,0.2)_inset,0_1px_0_rgba(0,0,0,0.05)] transition-transform hover:-translate-y-[1px] hover:bg-electric-hover disabled:opacity-70 disabled:hover:translate-y-0"
          >
            {loading ? "Scanning…" : "Scan"}
            <ArrowRight
              size={16}
              weight="bold"
              className="transition-transform group-hover:translate-x-0.5"
            />
          </button>
        </form>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
            try
          </span>
          {NICHE_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`suggest-${s.replace(/\s+/g, "-")}`}
              onClick={() => setNiche(s)}
              className="rounded-full border border-border bg-white px-2.5 py-1 text-xs text-ink transition-colors hover:border-electric hover:text-electric"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
